import { useMemo } from "react";
import useSWR from "swr";
import { compute } from "@/lib/finance";
import { usd, pct, pctFrac } from "@/lib/format";
import { Card, SectionHeader, Stat } from "@/components/common/Ornament";
import { RichHtml } from "@/components/common/RichText";
import ShortWarning from "@/components/common/ShortWarning";

export default function Benchmarks() {
  const { data: c } = useSWR("/content");
  const { data: fin } = useSWR("/financials");
  const r = useMemo(() => (fin ? compute(fin.baseline) : null), [fin]);

  return (
    <div className="space-y-8">
      <SectionHeader eyebrow="Market Context" title="Hotel Development Benchmarks" testId="benchmarks-header">
        Build-to-sell economics with a Hilton focus, and how this project's baseline compares.
      </SectionHeader>
      <ShortWarning testId="benchmarks-short-warning" />
      {r && (
        <Card eyebrow="Baseline Model" title="This Project vs. the Benchmarks" testId="benchmarks-subject">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Stat dark label="Cost per Key" value={usd(r.costPerKey)} sub="All-in, incl. land" testId="bench-cost-per-key" />
            <Stat label="Stabilized NOI per Key" value={usd(r.stabNoi / r.rooms)} sub="Year 3 (after ramp-up)" testId="bench-noi-per-key" />
            <Stat label="Yield on Cost" value={pctFrac(r.yieldOnCost)} sub="NOI ÷ total cost" testId="bench-yield-on-cost" />
            <Stat label="Exit Cap Rate" value={pct(fin.baseline.exit_cap)} sub="Model assumption" testId="bench-exit-cap" />
            <Stat label="Development Spread" value={r.devSpread === null ? "—" : `${(r.devSpread * 10000).toFixed(0)} bps`} sub="Yield on cost − exit cap" testId="bench-dev-spread" />
            <Stat label="Break-even NOI / Key" value={usd(r.costPerKey * (fin.baseline.exit_cap / 100))} sub="Cost/key × exit cap" testId="bench-breakeven-noi" />
          </div>
          <p className="text-xs text-slate-500 mt-4">Operating assumptions are placeholders pending the HVS feasibility study. Compare: HVS all-hotel median ~$213,000/key; Hilton New Orleans St. Charles sold at ~$187,000/key with ~$16,000 NOI/key.</p>
        </Card>
      )}
      <Card eyebrow="Prepared November 2026" title="Build-to-Sell Economics (Hilton Focus)" testId="benchmarks-content">
        <RichHtml html={c?.benchmarks_html} className="max-w-4xl" />
      </Card>
    </div>
  );
}
